import { useEffect, useRef, useState } from 'react';

/**
 * Returns `[ref, isStuck]` — attach the ref to a sticky-positioned
 * element and `isStuck` flips to `true` the moment the element pins
 * against whatever ceiling sits above it (Topbar, SectionNav, etc.).
 *
 * Implementation: IntersectionObserver with `threshold: [1]` and a
 * negative top `rootMargin` of `offsetPx + 1`. As long as the sticky
 * element is fully visible inside the shrunk root, the ratio stays at
 * 1 and `isStuck` is `false`. Once the element is pinned (i.e. it
 * intersects the shrunk root edge), the ratio drops below 1 and the
 * hook flips to `true`. The `+1` makes the transition fire the
 * moment the elements touch instead of one frame late.
 *
 * Why parameterize: the SectionNav (top: 56px) and the filter bars
 * (top: 116px = 56 + 60) sit at different sticky offsets, so the
 * shrunk root margin needs to mirror each call site. Common values:
 *   - 56  (SectionNav pinning under Topbar)
 *   - 116 (filter bars pinning under SectionNav + Topbar)
 *
 * Generic over the element type so callers can attach to `<nav>`,
 * `<div>`, etc. without a manual cast.
 *
 * Safe in SSR / non-DOM environments: bails out cleanly if
 * `IntersectionObserver` is undefined. Cleans up the observer on
 * unmount.
 */
export function useIsStuck<T extends Element = HTMLDivElement>(
  offsetPx: number,
) {
  const ref = useRef<T>(null);
  const [isStuck, setIsStuck] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) =>
        setIsStuck(entry !== undefined && entry.intersectionRatio < 1),
      {
        threshold: [1],
        rootMargin: `-${offsetPx + 1}px 0px 0px 0px`,
      },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [offsetPx]);
  return [ref, isStuck] as const;
}
